import { Injectable } from '@angular/core';
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root';
import { AuthStatePersistenceService, createFrom, EventService } from '@spartacus/core';
import { NavigationEvent } from '@spartacus/storefront';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Ga4GgCartPageEvent } from './ga4-gg-cart-page-event';

declare module '@spartacus/cart/base/root' {
  interface OrderEntry {
    totalPriceOnePiece?: number;
  }
}

@Injectable({
  providedIn: 'root',
})
export class Ga4GgCartPageEventBuilder {
  constructor(
    private eventService: EventService,
    private activeCartService: ActiveCartFacade,
    private authStatePersistenceService: AuthStatePersistenceService
  ) {
    const ga4GgCartPageCleanUpEventSource = this.eventService.get(NavigationEvent).pipe(
      filter((navigationEvent) => navigationEvent.semanticRoute === 'cart'),
      switchMap(() =>
        this.activeCartService.getActive().pipe(
          filter((activeCart) => Boolean(activeCart.entries)),
          take(1),
          map((activeCart) =>
            createFrom(Ga4GgCartPageEvent, {
              ecommerce: null,
            })
          )
        )
      )
    );

    eventService.register(Ga4GgCartPageEvent, ga4GgCartPageCleanUpEventSource);

    const ga4GgCartPageEventSource = this.eventService.get(NavigationEvent).pipe(
      filter((navigationEvent) => navigationEvent.semanticRoute === 'cart'),
      switchMap(() =>
        this.activeCartService.getActive().pipe(
          filter((activeCart) => Boolean(activeCart.entries)),
          take(1),
          map((activeCart) =>
            createFrom(Ga4GgCartPageEvent, {
              event: 'view_cart',
              visitor_type: authStatePersistenceService.isUserLoggedIn() ? 'logged_in' : 'logged-out',
              ecommerce: {
                currency: activeCart.totalPrice?.currencyIso,
                value: activeCart.totalPrice?.value,
                items: this.getItemsFromActiveCart(activeCart),
              },
            })
          )
        )
      )
    );

    eventService.register(Ga4GgCartPageEvent, ga4GgCartPageEventSource);
  }

  getItemsFromActiveCart(activeCart: Cart) {
    const items: {
      item_id?: string;
      item_name?: string;
      discount?: string;
      coupon?: string;
      price?: number;
      quantity?: number;
    }[] = [];

    activeCart.entries?.forEach((entry, index) => {
      const item = {
        item_id: entry.product?.code,
        item_name: entry.product?.modelMark,
        discount:
          entry.totalPriceOnePiece && entry.basePrice?.value && entry.totalPriceOnePiece < entry.basePrice?.value
            ? (entry.basePrice?.value - entry.totalPriceOnePiece).toFixed(2)
            : undefined,
        coupon: this.getAppliedVouchersFromCart(activeCart),
        price: entry.basePrice?.value,
        quantity: entry.quantity,
      };

      entry.product?.categoryHierarchy
        ?.slice()
        .reverse()
        .forEach((category, index) => {
          item['item_category' + (index.toString() !== '0' ? (index + 1).toString() : '')] = category.name;
        });

      Object.keys(item).forEach((key) => (item[key] === undefined ? delete item[key] : {}));

      items.push(item);
    });

    return items;
  }

  getAppliedVouchersFromCart(cart: Cart) {
    let appliedVouchers: string | undefined = undefined;

    cart.appliedVouchers?.forEach((voucher) => {
      appliedVouchers = appliedVouchers ? appliedVouchers + ',' + voucher.code : voucher.code;
    });

    return appliedVouchers;
  }
}
